import * as XLSX from "xlsx";
import * as XlsxPopulate from "xlsx-populate/browser/xlsx-populate";
import { IoDocumentOutline } from "react-icons/io5";

import Toaster from "./../../../components/common/Toaster";
import taxService from "./../../../services/taxService";
import moment from "moment";

export const CreditableExport = ({ id, account, data, options }) => {
  const fetchData = async () => {
    try {
      const total_count = data.length > 0 ? data[0].total_count : options.size;
      const params = { ...options };
      params["size"] = total_count;

      const { result } = await taxService.getAccountCreditablesAsync(
        id,
        params
      );

      const items = result.map((item) => {
        const {
          tin,
          atc,
          address,
          client_name,
          gross_amount,
          withheld_amount,
          net_amount,
          tax_rate,
          applicable_period: date,
        } = item;

        const result = {};
        result["tin"] = tin ?? "-";
        result["atc"] = atc ?? "-";
        result["client_name"] = client_name ?? "-";
        result["address"] = address ?? "-";
        result["tax_rate"] = `${tax_rate}%`;
        result["date"] = date ? moment(date).format("DD-MMM-YYYY") : "-";
        result["net_amount"] = net_amount;
        result["gross_amount"] = gross_amount;
        result["withheld_amount"] = withheld_amount;

        return result;
      });

      return items;
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  };

  const workbook2blob = (workbook) => {
    const wopts = {
      bookType: "xlsx",
      bookSST: false,
      type: "binary",
    };

    const wbout = XLSX.write(workbook, wopts);

    const blob = new Blob([s2ab(wbout)], {
      type: "application/octet-stream",
    });

    return blob;
  };

  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);

    for (let i = 0; i !== s.length; ++i) {
      view[i] = s.charCodeAt(i);
    }

    return buf;
  };

  const handleExport = async () => {
    const creditables = await fetchData();

    const table = [
      {
        A: "2307 DATE",
        B: "TIN",
        C: "CLIENT'S NAME",
        D: "ADDRESS",
        E: "GROSS AMOUNT",
        F: "CWT",
        G: "WT RATE",
        H: "NET OF CWT",
        I: "ATC",
      },
    ];

    creditables.forEach((item) => {
      table.push({
        A: item.date,
        B: item.tin,
        C: item.client_name,
        D: item.address,
        E: item.gross_amount,
        F: item.withheld_amount,
        G: item.tax_rate,
        H: item.net_amount,
        I: item.atc,
      });
    });

    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(table, {
      skipHeader: true,
    });

    XLSX.utils.book_append_sheet(workbook, sheet, "2307");

    const workbookBlob = workbook2blob(workbook);
    const total = creditables.length + 1;

    const info = {
      bodyRange: `A1:I${table.length}`,
      headRange: `A1:I1`,
      firstColumn: `A1:A${total}`,
      lastColumn: `I:I${total}`,
      leftColumns: `C2:D${total}`,
      rightColumns: `E2:H${total}`,
    };

    return addStyle(workbookBlob, info, total);
  };

  const addStyle = (workbookBlob, info, total) => {
    return XlsxPopulate.fromDataAsync(workbookBlob).then((workbook) => {
      workbook.sheets().forEach((sheet) => {
        sheet.usedRange().style({
          fontFamily: "Calibri",
          verticalAlignment: "center",
        });

        const getMaxStringLength = (range) => {
          return sheet.range(range).reduce((max, cell) => {
            const value = cell.value();
            if (value === undefined) return max;
            return Math.max(max, value.toString().length);
          }, 0);
        };

        sheet.column("A").width(12);
        sheet.column("B").width(14);
        sheet.column("C").width(getMaxStringLength(`C1:C${total}`));
        sheet.column("D").width(40);
        sheet.column("E").width(15);
        sheet.column("F").width(15);
        sheet.column("H").width(15);

        sheet.range(info.headRange).style({
          fill: "808080",
          horizontalAlignment: "center",
          fontColor: "ffffff",
        });

        sheet.range(info.bodyRange).style({
          horizontalAlignment: "center",
        });

        sheet.range(info.leftColumns).style({
          horizontalAlignment: "left",
        });

        sheet.range(info.rightColumns).style({
          horizontalAlignment: "right",
          numberFormat: "#,###",
        });
      });

      return workbook
        .outputAsync()
        .then((workbookBlob) => URL.createObjectURL(workbookBlob));
    });
  };

  const createDownloadData = () => {
    handleExport().then((url) => {
      const year = options.year ? ` ${options.year}` : "";
      const quarters = taxService.getAltTaxQuarters();
      const quarter = options.quarter
        ? ` ${quarters.filter((i) => i.value === options.quarter)[0].label}`
        : "";

      const file_name = `2307 - ${account.short_name}${year}${quarter}`;

      const downloadAnchorNode = document.createElement("a");
      downloadAnchorNode.setAttribute("href", url);
      downloadAnchorNode.setAttribute("download", file_name);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    });
  };

  return (
    <div>
      <button
        title="Export to Excel"
        className="button cursor-pointer h-10 border group hover:border-blue-500"
        onClick={() => createDownloadData()}
      >
        <IoDocumentOutline className="text-gray-400 group-hover:text-blue-500" />
      </button>
    </div>
  );
};

export default CreditableExport;
